import { Input, Modal } from 'antd';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { userInfo } from '../../../../models/entities/user';
import { platformUsersSelectors } from '../../../../state/ducks/platformUsers/selectors';
import { platformCompaniesSelectors } from '../../../../state/ducks/platformCompanies/selectors';
import { RootState } from '../../../../state/store/store';
import Grid from '../../../shared/Grid/Grid';
import { ColumnType } from '../../../shared/Grid/types/Column';
import Button from '../../../shared/SmallComponents/Button';
import Loader from '../../../shared/SmallComponents/Loader';
import {
    PlatformActions,
    PlatformErrorBanner,
    PlatformGridWrapper,
    PlatformHeaderRow,
    PlatformHeaderWrapper,
    PlatformPageSubtitle,
    PlatformPageTitle,
    PlatformPageWrapper
} from '../shared/platformPageStyles';

const PlatformUsersPageConnected = ({
    users,
    companies,
    showLoader,
    gridFilter,
    lastError,
    fetchUsers,
    fetchCompanies,
    setGridFilter,
    saveUser
}: any) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [editing, setEditing] = useState<Partial<userInfo> & { password?: string } | null>(null);

    useEffect(() => {
        fetchUsers();
        fetchCompanies();
    }, [fetchUsers, fetchCompanies]);

    const columns: Array<ColumnType<userInfo>> = [
        {
            field: 'firstName',
            width: '180px',
            minWidth: '140px',
            sortable: true,
            bindedFields: ['lastName'],
            renderer: (f: string, b?: string[]) => <span>{`${f || ''} ${b?.[0] || ''}`.trim()}</span>
        },
        { field: 'email', width: '100%', minWidth: '180px', sortable: true },
        { field: 'companyName', width: '100%', minWidth: '140px', sortable: true },
        { field: 'role', width: '100%', minWidth: '100px', sortable: false },
        {
            field: 'id',
            title: 'Actions',
            width: '90px',
            textAlign: 'center',
            sortable: false,
            bindedFields: ['email', 'firstName', 'lastName', 'companyId', 'role', 'phoneNumber', 'username'],
            renderer: (_id: string, binded?: Array<any>) => (
                <ActionBtn
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        setEditing({
                            id: _id,
                            email: binded?.[0],
                            firstName: binded?.[1],
                            lastName: binded?.[2],
                            companyId: binded?.[3],
                            role: binded?.[4],
                            phoneNumber: binded?.[5],
                            username: binded?.[6]
                        });
                        setModalOpen(true);
                    }}
                >
                    Edit
                </ActionBtn>
            )
        }
    ];

    return (
        <PlatformPageWrapper>
            <PlatformHeaderWrapper>
                <PlatformHeaderRow>
                    <div>
                        <PlatformPageTitle>All users</PlatformPageTitle>
                        <PlatformPageSubtitle>
                            Search and manage people across every organization from the platform portal.
                        </PlatformPageSubtitle>
                    </div>
                    <PlatformActions>
                        <Input
                            placeholder="Search name, email, or company"
                            value={gridFilter}
                            onChange={(e) => setGridFilter(e.target.value)}
                            style={{ width: 280 }}
                        />
                        <Button buttonType="hollow" onClick={() => fetchUsers()}>
                            Refresh
                        </Button>
                        <Button
                            buttonType="filled"
                            onClick={() => {
                                setEditing({
                                    email: '',
                                    firstName: '',
                                    lastName: '',
                                    role: '',
                                    companyId: companies[0]?.id
                                });
                                setModalOpen(true);
                            }}
                        >
                            Add user
                        </Button>
                    </PlatformActions>
                </PlatformHeaderRow>
            </PlatformHeaderWrapper>
            {lastError && <PlatformErrorBanner>{lastError}</PlatformErrorBanner>}
            <PlatformGridWrapper>
                {showLoader ? (
                    <Loader />
                ) : (
                    <Grid
                        columns={columns}
                        data={users}
                        localization={{ fields: { firstName: 'Name', email: 'Email', companyName: 'Company', role: 'Role', id: 'Actions' } }}
                        emptyStateText="No users found"
                    />
                )}
            </PlatformGridWrapper>
            <Modal
                title={editing?.id ? 'Edit user' : 'Create user'}
                open={modalOpen}
                onCancel={() => setModalOpen(false)}
                onOk={async () => {
                    if (!editing?.email || !editing?.companyId) return;
                    await saveUser({
                        id: editing.id,
                        data: {
                            email: editing.email,
                            firstName: editing.firstName,
                            lastName: editing.lastName,
                            role: editing.role,
                            phoneNumber: editing.phoneNumber,
                            companyId: editing.companyId,
                            username: editing.username || editing.email,
                            password: editing.password
                        }
                    });
                    setModalOpen(false);
                }}
                width={520}
            >
                <FormField label="Email">
                    <Input value={editing?.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} />
                </FormField>
                {!editing?.id && (
                    <FormField label="Password">
                        <Input.Password value={editing?.password} onChange={(e) => setEditing({ ...editing, password: e.target.value })} />
                    </FormField>
                )}
                <FormField label="First name">
                    <Input value={editing?.firstName} onChange={(e) => setEditing({ ...editing, firstName: e.target.value })} />
                </FormField>
                <FormField label="Last name">
                    <Input value={editing?.lastName} onChange={(e) => setEditing({ ...editing, lastName: e.target.value })} />
                </FormField>
                <FormField label="Company">
                    <select
                        value={editing?.companyId}
                        onChange={(e) => setEditing({ ...editing, companyId: e.target.value })}
                        style={{ width: '100%', height: 32 }}
                    >
                        {companies.map((c: { id: string; name: string }) => (
                            <option key={c.id} value={c.id}>
                                {c.name}
                            </option>
                        ))}
                    </select>
                </FormField>
                <FormField label="Role">
                    <Input value={editing?.role} onChange={(e) => setEditing({ ...editing, role: e.target.value })} />
                </FormField>
            </Modal>
        </PlatformPageWrapper>
    );
};

const FormField = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <FieldWrap>
        <FieldLabel>{label}</FieldLabel>
        {children}
    </FieldWrap>
);
const FieldWrap = styled.div`margin-bottom: 12px;`;
const FieldLabel = styled.label`display: block; margin-bottom: 4px; font-weight: 600;`;
const ActionBtn = styled.button`
    border: none;
    background: none;
    color: ${(p) => p.theme.colors.link};
    cursor: pointer;
`;

const mapState = (state: RootState) => ({
    users: platformUsersSelectors.filteredUsers(state),
    companies: platformCompaniesSelectors.companies(state),
    gridFilter: platformUsersSelectors.gridFilter(state),
    lastError: platformUsersSelectors.lastError(state),
    showLoader: !!state.loading.effects.platformUsers?.fetchUsers
});

const mapDispatch = (dispatch: any) => ({
    fetchUsers: () => dispatch.platformUsers.fetchUsers(),
    fetchCompanies: () => dispatch.platformCompanies.fetchCompanies(),
    setGridFilter: (v: string) => dispatch.platformUsers.setGridFilter(v),
    saveUser: (p: { id?: string; data: Record<string, unknown> }) => dispatch.platformUsers.saveUser(p)
});

export default connect(mapState, mapDispatch)(PlatformUsersPageConnected);
